import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormStatus, LANGUAGES, LOCALE_LOCALIZATION_KEYS_MAP } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { localizationKeys } from '../../constants/localizationKeys';
import { SignupHelper } from '../../helpers/signup.helper';
import { ValidatorsHelper } from '../../helpers/validators.helper';
import { IPersonalInformation } from '../../interfaces/personal-information.interface';

const DEUTSCH_INDEX = 0;
const ENGLISH_INDEX = 1;

@UntilDestroy()
@Component({
    selector: 'aumico-users-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./personal-information.component.scss'],
})
export class PersonalInformationComponent implements OnInit {
    form: UntypedFormGroup;
    languagesSelections: INameId[] = [];
    localizations = localizationKeys;
    private browserLanguage: string | undefined;
    private preferredLanguage: string | null;

    @Input() set supportedLocales(locales: string[]) {
        this.setLanguage(locales);
    }
    @Input() defaultLanguage = LANGUAGES.ENGLISH;
    @Input() organization?: string;
    @Output() personalInformationData = new EventEmitter<IPersonalInformation>();
    @Output() personaInformationFormStatus = new EventEmitter<boolean>();

    constructor(private readonly formBuilder: UntypedFormBuilder, private readonly i18nextService: I18NextService) {
        this.browserLanguage = navigator.language.toLowerCase();
        const pathSegments = window.location.pathname.split('/');
        // The language is in the URL path, e.g., /en/register
        if (pathSegments.length > 1 && pathSegments[1]) {
            this.preferredLanguage = pathSegments[1];
        } else {
            // Fallback for the case where it might still be a query param
            const urlParams = new URLSearchParams(window.location.search);
            this.preferredLanguage = urlParams.get('preferredLanguage');
        }
    }

    ngOnInit(): void {
        this.initializePersonalInformationForm();
        this.detectFormStatusChanges();
        this.detectLanguageChange();
    }

    private initializePersonalInformationForm(): void {
        this.form = this.formBuilder.group({
            organizationName: [
                { value: this.organization, disabled: this.organization != null },
                {
                    validators: [Validators.required, ValidatorsHelper.validateEmptyString()],
                },
            ],
            firstName: [null, { validators: [Validators.required, ValidatorsHelper.validateEmptyString()] }],
            lastName: [null, { validators: [Validators.required, ValidatorsHelper.validateEmptyString()] }],
            locale: [null, { validators: [Validators.required] }],
        });
    }

    private detectFormStatusChanges(): void {
        this.form.statusChanges.pipe(untilDestroyed(this)).subscribe((status) => {
            const isFormValid = status === FormStatus.VALID;
            this.personaInformationFormStatus.emit(isFormValid);
            if (isFormValid) {
                // getRawValue to get also the value of the disabled fields
                const rawFormValue = this.form.getRawValue();
                const personalInformationData = SignupHelper.preparePersonalInformationFormData(rawFormValue);
                this.personalInformationData.emit(personalInformationData);
            }
        });
    }

    private detectLanguageChange(): void {
        this.form
            .get('locale')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((selectedLocale: INameId) => {
                if (this.form.get('locale').dirty && selectedLocale) {
                    const url = new URL(window.location.href);
                    const pathSegments = url.pathname.split('/');
                    // Assuming the language segment is always at index 1
                    if (pathSegments.length > 1) {
                        pathSegments[1] = selectedLocale.id;
                        url.pathname = pathSegments.join('/');
                        // The preferredLanguage parameter is not needed anymore
                        url.searchParams.delete('preferredLanguage');
                        window.location.href = url.toString();
                    }
                }
            });
    }

    private setLanguage(supportedLocales: string[]): void {
        supportedLocales.forEach((locale) => {
            this.languagesSelections.push({
                id: locale,
                name: this.i18nextService.t(LOCALE_LOCALIZATION_KEYS_MAP.get(locale.toLocaleLowerCase())) as string,
            });
        });

        let languageToBeSelected: INameId | undefined;

        // Prioritize language from URL path
        if (this.preferredLanguage) {
            languageToBeSelected = this.languagesSelections.find(
                (language) => language.id.toLowerCase() === this.preferredLanguage.toLowerCase(),
            );
        }

        // Fallback to browser language
        if (!languageToBeSelected) {
            const isBrowserLanguageExistInAvailableLanguage = supportedLocales.find(
                (language) => language === this.browserLanguage,
            );
            if (this.browserLanguage != null && isBrowserLanguageExistInAvailableLanguage) {
                languageToBeSelected = this.languagesSelections.find(
                    (language) => language.id === this.browserLanguage,
                );
            }
        }

        // Fallback to default language
        if (!languageToBeSelected) {
            languageToBeSelected = this.languagesSelections.find((language) => language.id === this.defaultLanguage);
        }

        if (this.form != null) {
            this.form.get('locale')?.setValue(languageToBeSelected);
        }

        this.languagesSelections = this.languagesSelections.filter((languageSelection) => languageSelection != null);
    }

    public compareSelections(object1: INameId, object2: INameId): boolean {
        return object1.id === object2.id;
    }
}
